import { Fragment } from 'react';
import Link from 'next/link';
import PagePaperlogyFontFace from '@/features/common/components/PagePaperlogyFontFace';
import { pageAsset } from '@/features/common/pageAssets';
import { SubjectDesign, SubjectPage, SubjectText } from './SubjectContent.types';
import * as S from './SubjectContent.styled';

const textId = (page: SubjectPage, key: string) => `subject-${page}-${key.replace(':', '-')}`;
const textValue = (block: SubjectText) =>
    block.runs.map((run, i) => `${i && run.top !== block.runs[i - 1].top ? ' ' : ''}${run.text}`).join('');

/** 분리 이미지의 빈 텍스트 영역을 선택·검색 가능한 HTML 본문으로 채운다. */
function Copy({
    page,
    block,
    offset = 0,
    leftOffset = 0,
}: {
    page: SubjectPage;
    block: SubjectText;
    offset?: number;
    leftOffset?: number;
}) {
    const bullet = block.bullet;
    const runs = (
        <>
            {block.runs.map((run, index) => (
                <Fragment key={index}>
                    {index > 0 && run.top !== block.runs[index - 1].top && <br />}
                    <span
                        style={{
                            position: 'absolute',
                            left: run.left,
                            top: run.top,
                            fontFamily: `${run.font || block.font}, Pretendard, sans-serif`,
                            fontSize: run.size ?? block.size,
                            fontWeight: run.weight ?? block.weight,
                            letterSpacing: run.letterSpacing ?? block.letterSpacing,
                            color: run.color || block.color,
                        }}
                    >
                        {run.text.split('∙').map((text, part) => (
                            <Fragment key={part}>
                                {part > 0 && (
                                    <S.Separator
                                        paperlogy={(run.font || block.font) === 'Paperlogy'}
                                        inkColor={run.color || block.color}
                                        tracking={run.letterSpacing ?? block.letterSpacing}
                                    >
                                        ∙
                                    </S.Separator>
                                )}
                                {text}
                            </Fragment>
                        ))}
                    </span>
                </Fragment>
            ))}
        </>
    );
    return (
        <S.Copy
            as={bullet ? 'ul' : block.tag}
            id={textId(page, block.key)}
            aria-label={block.tag !== 'p' ? textValue(block) : undefined}
            style={{
                left: block.left - leftOffset,
                top: block.top - offset,
                width: block.width,
                height: block.height,
                lineHeight: `${block.lineHeight}px`,
                textShadow: block.shadow,
                fontFamily: `${block.font}, Pretendard, sans-serif`,
                fontSize: block.size,
                letterSpacing: block.letterSpacing,
                color: block.color,
            }}
        >
            {bullet ? <li>{runs}</li> : runs}
        </S.Copy>
    );
}

export default function SubjectContent({ page, design }: { page: SubjectPage; design: SubjectDesign }) {
    const linkCopy = design.texts.find((block) => block.key === design.link.key)!;
    const decorationShift = design.decorationShift;
    // 삭제한 구간 간격만큼 본문도 줄이되, 장식 SVG의 원본 높이는 유지한다.
    const contentHeight = design.height + (decorationShift?.by ?? 0);
    return (
        <>
            <PagePaperlogyFontFace />
            <S.Content aria-labelledby={textId(page, design.heading)} style={{ height: contentHeight }}>
                <S.BackgroundCanvas aria-hidden="true" style={{ height: design.height }}>
                    {design.backgrounds.map((background, i) => (
                        <S.Background
                            key={i}
                            style={{
                                top: background.top,
                                height: background.height,
                                backgroundColor: background.color,
                                backgroundImage: background.file
                                    ? `url("${pageAsset(page, background.file)}")`
                                    : undefined,
                            }}
                        />
                    ))}
                </S.BackgroundCanvas>
                <S.Canvas>
                    {design.artworks.map((artwork, i) => (
                        <S.Image
                            key={artwork.file}
                            src={pageAsset(page, artwork.file)}
                            alt={artwork.alt}
                            width={artwork.width}
                            height={artwork.height}
                            style={{
                                left: artwork.left,
                                top: artwork.top,
                                clipPath: artwork.clipBottom ? `inset(0 0 ${artwork.clipBottom}px)` : undefined,
                            }}
                            loading={i === 0 ? 'eager' : 'lazy'}
                        />
                    ))}
                    {design.animations.map((animation) => (
                        <S.Animation
                            key={animation.file}
                            src={pageAsset(page, animation.file)}
                            alt={animation.alt}
                            width={animation.width}
                            height={animation.height}
                            style={{ left: animation.left, top: animation.top }}
                            loading="lazy"
                        />
                    ))}
                    <S.Decorations
                        src={pageAsset(page, 'decorations.svg')}
                        width={2560}
                        height={design.height}
                        alt=""
                        aria-hidden="true"
                        style={
                            decorationShift
                                ? { clipPath: `inset(0 0 ${design.height - decorationShift.top}px)` }
                                : undefined
                        }
                    />
                    {decorationShift && (
                        <S.Decorations
                            src={pageAsset(page, 'decorations.svg')}
                            width={2560}
                            height={design.height}
                            alt=""
                            aria-hidden="true"
                            style={{ top: decorationShift.by, clipPath: `inset(${decorationShift.top}px 0 0)` }}
                        />
                    )}
                    {design.sections.map((section) => {
                        const texts = design.texts.filter(
                            (block) =>
                                block.key !== design.link.key &&
                                block.top >= section.top &&
                                block.top < section.top + section.height
                        );
                        const heading = texts.find((block) => block.tag === 'h1' || block.tag === 'h2');
                        return (
                            <S.Section
                                key={section.key}
                                style={{ top: section.top, height: section.height }}
                                aria-labelledby={heading ? textId(page, heading.key) : undefined}
                            >
                                {(() => {
                                    const renderCopy = (block: SubjectText) => (
                                        <Copy key={block.key} page={page} block={block} offset={section.top} />
                                    );
                                    const groups = section.groups || [];
                                    const groupedKeys = new Set(
                                        groups.flatMap((group) =>
                                            group.kind === 'list' ? group.items.flat() : group.keys
                                        )
                                    );
                                    const renderKeys = (keys: string[]) =>
                                        keys.map((key) => {
                                            const block = texts.find((text) => text.key === key);
                                            return block ? renderCopy(block) : null;
                                        });
                                    return (
                                        <>
                                            {texts.filter((block) => !groupedKeys.has(block.key)).map(renderCopy)}
                                            {groups.map((group, index) =>
                                                group.kind === 'list' ? (
                                                    <S.TextList key={index} aria-label={group.label}>
                                                        {group.items.map((keys, item) => (
                                                            <li key={item}>{renderKeys(keys)}</li>
                                                        ))}
                                                    </S.TextList>
                                                ) : (
                                                    <section key={index} aria-labelledby={textId(page, group.heading)}>
                                                        {renderKeys(group.keys)}
                                                    </section>
                                                )
                                            )}
                                        </>
                                    );
                                })()}
                            </S.Section>
                        );
                    })}
                    <Link href={design.link.href} legacyBehavior passHref>
                        <S.PageLink
                            id={design.link.id}
                            aria-label={textValue(linkCopy)}
                            style={{
                                left: design.link.left,
                                top: design.link.top,
                                width: design.link.width,
                                height: design.link.height,
                            }}
                        >
                            <Copy page={page} block={linkCopy} offset={design.link.top} leftOffset={design.link.left} />
                        </S.PageLink>
                    </Link>
                </S.Canvas>
            </S.Content>
        </>
    );
}
